// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// App-wide freshness driver for the alarm and anomaly lists (ADR-0013).
//
// Mounted once in the shell rather than per page, for two reasons:
//
//   • One timer per query key. Every observer of a query runs its OWN refetch
//     interval, so a 10s page observer plus a 60s shell observer would fire on
//     both cadences against the same key. Deciding the interval from the route
//     in a single place keeps it to one.
//   • The queries stay observed across navigation, so returning to the list
//     paints a recent cache entry instead of the CardSkeleton grid.
//
// This is the same "warm a shared cache entry from the persistent shell" pattern
// as useTwinPrefetch / useApprovalsPrefetch, with a poll attached.

import { useLocation } from 'react-router-dom';
import {
  ALARMS_ACTIVE_POLL_INTERVAL_MS,
  ALARMS_BACKGROUND_POLL_INTERVAL_MS,
} from '../config/queryConfig';
import { useActiveSite } from './useActiveSite';
import { useAnomalies } from './useAnomalies';
import { useConsoleAlarms } from './useConsoleAlarms';

/**
 * The Home routes that render an AlarmList (see AppRoutes' `/home/:bucket` and
 * alarmSelectors' ALARM_TABS). `recommendations` is deliberately absent: that
 * tab lists plans, not alarms, and only borrows the tab bar's count badges.
 */
const ALARM_LIST_ROUTE = /^\/home\/(alarms|anomalies|routine|closed)\b/;

/** True when `pathname` is a route whose primary content is an alarm list. */
export function isAlarmListRoute(pathname: string): boolean {
  return ALARM_LIST_ROUTE.test(pathname);
}

/**
 * Keep the alarm and anomaly caches fresh: fast while an alarm list is on
 * screen, slow otherwise. Returns nothing — callers read the data through
 * useConsoleAlarms / useAnomalies as usual and join this same query.
 */
export function useAlarmsAutoRefresh(): void {
  const { siteId } = useActiveSite();
  const { pathname } = useLocation();

  const pollIntervalMs = isAlarmListRoute(pathname)
    ? ALARMS_ACTIVE_POLL_INTERVAL_MS
    : ALARMS_BACKGROUND_POLL_INTERVAL_MS;

  useConsoleAlarms(siteId, { pollIntervalMs });
  useAnomalies(siteId, { pollIntervalMs });
}
